import type { PerceivedEvent } from '@/sim/events';
import type { AbsenceEstimate, AbsenceRun } from '@/sim/persistence/catchup';

/** An absence being paid for: the run, what it was estimated at, and the
 *  world-time it started from. The run is the world-bound part. */
export interface Crossing {
  readonly run: AbsenceRun;
  readonly est: AbsenceEstimate;
  readonly leftAt: number;
}

/**
 * WHAT THE CLIENT HOLDS ABOUT THE WORLD IT IS CURRENTLY IN.
 *
 * `main.ts` keeps two kinds of state and has never distinguished them. Most of
 * it is **self-healing**: `hovered` is re-picked every frame, `litHearths` is
 * rebuilt every frame, `lastSereStore` is compared by identity and swaps
 * itself. Adopting a world cannot hurt any of that, because none of it
 * survives the next frame.
 *
 * The rest is **world-bound**: it names, counts or points into one particular
 * `Simulation`, and nothing re-derives it. When a world is replaced and a slot
 * like that is left alone, the client goes on quietly answering questions
 * about a world that no longer exists — and every symptom is a plausible-
 * looking lie rather than a crash. A Chronicle cursor into a discarded event
 * ring. A set of act ids saying *this was your hand* about somebody else's
 * history. A catch-up run advancing, in the background, a `Simulation` that
 * has already been thrown away.
 *
 * There is nothing subtle about any of those bugs individually. What made them
 * a *family* — four rows of `WIRING.md` at once, all live in the shipped game,
 * which always adopts prehistory at boot — is that the reset lived at the
 * bottom of a 4,151-line module reachable by zero suites, so the only way to
 * find a missing line was to read for it.
 *
 * So the slots live here instead, in one table, and:
 *
 *  · `adopt()` empties every slot the table declares, by construction. There
 *    is no list of assignments for somebody to forget a line out of.
 *  · `WORLD_BOUND` is exported, so `test/adoption.ts` can walk it, and can
 *    also assert that it **covers every own field of the session**. Adding a
 *    ninth slot without declaring it fails the gate rather than shipping.
 *
 * Deliberately **DOM-free and three-free**: it is constructible under `tsx`,
 * which is the whole reason the gate can exist at all.
 *
 * Not here, and on purpose: observation state — the chosen subject, the eye
 * following it, an arrival in progress. `letGo()` already owns that in
 * `main.ts`, it is called from more places than adoption, and it holds a
 * `Target` whose meaning is a render-side pick. Attention is not world-bound
 * in the same sense either: it is a thing the *player* is doing, and the reset
 * on adoption is a courtesy (you cannot go on watching somebody who was never
 * born) rather than a correctness fix.
 */

/** A world the session can be pointed at. Structural, so a suite may fake it. */
export interface SessionWorld {
  readonly events: { all(): ReadonlyArray<unknown> };
}

/**
 * One slot: how to tell it is empty, and how to empty it.
 *
 * `empty` is not decoration for the test — it is what makes the gate able to
 * say *which* slot survived, rather than only that something did.
 */
interface Slot {
  /** Why this slot refers to one particular world. Read by nobody; kept for the reader. */
  readonly why: string;
  empty(s: BoundSlots): boolean;
  clear(s: BoundSlots): void;
}

/** The world-bound half of a session, structurally — what the table acts on. */
interface BoundSlots {
  readonly acts: Set<string>;
  readonly feed: PerceivedEvent[];
  cursor: string;
  hold: Hold;
  dig: Hold;
  crossing: Crossing | null;
}

/**
 * A hold is whatever the hand has closed on — a `Target` for an object, a
 * pending scoop for the ground. The session never looks inside one: it only
 * has to know that a hand cannot keep its grip across a change of world.
 */
export type Hold = unknown;

export class WorldSession<W extends SessionWorld = SessionWorld> {
  /** The world itself. Everything below is scoped to exactly this object. */
  private current: W;

  /**
   * The god's own record of its own hand — `Simulation` event ids.
   *
   * A god knows what it did because it did it, never because it read the
   * world's ledger (law 10), which is what lets the Chronicle show your own
   * miracle from the other side of the island. Ids mint as
   * `type@time#seq`, so the same id can legitimately be minted again by a
   * different world; carrying the set across an adoption is how a god comes
   * to be credited with a stranger's lightning.
   */
  readonly acts = new Set<string>();

  /** The Chronicle feed as this client has perceived it. */
  readonly feed: PerceivedEvent[] = [];

  /**
   * How far into the world's event ring the feed has read.
   *
   * An id, not an index, because the ring turns over. Pointed at the
   * discarded world it resolves to nothing, `noteEvents` falls back to the
   * start of the ring, and the player is handed a few hundred of the new
   * world's old events as though they had all just happened.
   */
  cursor = '';

  /** What the hand has closed on, and what it has closed on the ground. */
  hold: Hold = null;
  dig: Hold = null;

  /**
   * The absence being paid for, which **closes over its `Simulation` by
   * value**. `runAbsence(sim, …)` captured `sim` when it was made; loading a
   * world mid-catch-up leaves it advancing the world that was replaced, and
   * every world-second it spends there is spent nowhere the player can see.
   */
  crossing: Crossing | null = null;

  constructor(world: W) { this.current = world; }

  get world(): W { return this.current; }

  /**
   * Take a whole world in place of this one.
   *
   * The caller still owns everything that is not world-bound — the chunks that
   * carry the old world's history, the far field, the reach the restored god
   * flies with, the observation state. This owns the slots that have no other
   * home, and it owns them exhaustively.
   */
  adopt(next: W): void {
    this.current = next;
    for (const slot of Object.values(WORLD_BOUND)) slot.clear(this);
  }

  /** Which slots still refer to a world. Empty is the only correct answer
   *  after an adoption, and the gate reads exactly this. */
  stillBound(): string[] {
    return Object.entries(WORLD_BOUND)
      .filter(([, slot]) => !slot.empty(this))
      .map(([name]) => name);
  }
}

/**
 * THE TABLE. Every field of `WorldSession` that names one world, and nothing
 * else — `test/adoption.ts` asserts that literally, by reflecting over a
 * session instance, so this cannot silently fall behind the class.
 */
export const WORLD_BOUND: Readonly<Record<string, Slot>> = {
  acts: {
    why: 'event ids minted by one world',
    empty: (s) => s.acts.size === 0,
    clear: (s) => s.acts.clear(),
  },
  feed: {
    why: 'events perceived out of one world',
    empty: (s) => s.feed.length === 0,
    clear: (s) => { s.feed.length = 0; },
  },
  cursor: {
    why: 'a position in one world\'s event ring',
    empty: (s) => s.cursor === '',
    clear: (s) => { s.cursor = ''; },
  },
  hold: {
    why: 'a hand closed on one world\'s object',
    empty: (s) => s.hold === null,
    clear: (s) => { s.hold = null; },
  },
  dig: {
    why: 'a hand closed on one world\'s ground',
    empty: (s) => s.dig === null,
    clear: (s) => { s.dig = null; },
  },
  crossing: {
    why: 'a catch-up run holding one Simulation by value',
    empty: (s) => s.crossing === null,
    clear: (s) => { s.crossing = null; },
  },
};

/**
 * The fields of a session that are NOT world-bound, so the gate can tell a new
 * slot from a new convenience. `current` is the world itself and is set by
 * `adopt` rather than cleared by it.
 */
export const NOT_BOUND: readonly string[] = ['current'];
